import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const emptyClient = {
  name: '',
  email: '',
  phone: '',
  address: '',
  contactPerson: '',
  notes: ''
};

const Clients = () => {
  const navigate = useNavigate();
  const { canAccess, hasRole } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', isActive: 'true' });
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [selectedClient, setSelectedClient] = useState(null);

  const canManageClients = hasRole(['admin', 'accounts']);
  const canDeleteClients = hasRole('admin');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: emptyClient });

  const cleanPayload = (data) => {
    const payload = {};
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) payload[key] = trimmed;
      } else if (value !== undefined && value !== null && value !== '') {
        payload[key] = value;
      }
    });
    return payload;
  };

  const fetchClients = useCallback(async () => {
    if (!canAccess('clients')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100', isActive: filters.isActive });
      if (filters.search.trim()) params.append('search', filters.search.trim());

      const response = await api.get(`/api/clients?${params.toString()}`);
      setClients(response.data.clients || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  }, [canAccess, filters]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const totals = useMemo(() => {
    return clients.reduce(
      (acc, client) => {
        acc.dues += Number(client.totalDues) || 0;
        if (client.totalDues > 0) acc.withDues += 1;
        return acc;
      },
      { count: clients.length, dues: 0, withDues: 0 }
    );
  }, [clients]);

  const openModal = (type, client = null) => {
    setModalType(type);
    setSelectedClient(client);

    if (type === 'edit' && client) {
      reset({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        contactPerson: client.contactPerson || '',
        notes: client.notes || ''
      });
    } else {
      reset(emptyClient);
    }

    setShowModal(true);
  };

  const onSubmit = async (data) => {
    const payload = cleanPayload(data);

    try {
      if (modalType === 'create') {
        await api.post('/api/clients', payload);
        toast.success('Client created');
      } else {
        await api.put(`/api/clients/${selectedClient._id}`, payload);
        toast.success('Client updated');
      }

      setShowModal(false);
      reset(emptyClient);
      fetchClients();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to save client');
    }
  };

  const deleteClient = async (client) => {
    if (!window.confirm(`Deactivate ${client.name}?`)) return;

    try {
      await api.delete(`/api/clients/${client._id}`);
      toast.success('Client deactivated');
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to deactivate client');
    }
  };

  if (!canAccess('clients')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-600">Open a client account to review ledger activity and dues</p>
        </div>
        {canManageClients && (
          <button onClick={() => openModal('create')} className="btn-primary flex items-center justify-center">
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Client
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Visible Clients</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{totals.count}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Total Dues</p>
          <p className="mt-1 text-2xl font-semibold text-red-600">{formatCurrency(totals.dues)}</p>
        </div>
        <div className="card p-4">
          <p className="text-sm font-medium text-gray-500">Clients With Dues</p>
          <p className="mt-1 text-2xl font-semibold text-yellow-600">{totals.withDues}</p>
        </div>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={filters.search}
              onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
              className="input-field !pl-11"
              placeholder="Search name, email, contact person"
            />
          </div>
          <select
            value={filters.isActive}
            onChange={(event) => setFilters((prev) => ({ ...prev, isActive: event.target.value }))}
            className="input-field"
          >
            <option value="true">Active clients</option>
            <option value="false">Inactive clients</option>
          </select>
          <button
            onClick={() => setFilters({ search: '', isActive: 'true' })}
            className="btn-secondary flex items-center justify-center"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Reset
          </button>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ledger</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No clients found</td>
                </tr>
              ) : (
                clients.map((client) => (
                  <tr
                    key={client._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/clients/${client._id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.contactPerson || 'No contact person'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{client.email || 'No email'}</div>
                      <div className="text-sm text-gray-500">{client.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-semibold ${client.totalDues > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(client.totalDues)}
                      </div>
                      <div className="text-xs text-gray-500">Current dues</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {client.createdBy?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2" onClick={(event) => event.stopPropagation()}>
                        <button
                          onClick={() => navigate(`/clients/${client._id}`)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Open ledger"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        {canManageClients && (
                          <button
                            onClick={() => openModal('edit', client)}
                            className="text-yellow-600 hover:text-yellow-900"
                            title="Edit client"
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                        )}
                        {canDeleteClients && client.isActive !== false && (
                          <button
                            onClick={() => deleteClient(client)}
                            className="text-red-600 hover:text-red-900"
                            title="Deactivate client"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalType === 'create' ? 'Add Client' : 'Edit Client'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Client Name</label>
                  <input
                    {...register('name', { required: 'Client name is required' })}
                    className="input-field"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="label">Contact Person</label>
                  <input {...register('contactPerson')} className="input-field" />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input
                    {...register('email', {
                      pattern: { value: /^\S+@\S+$/i, message: 'Enter a valid email' }
                    })}
                    className="input-field"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input {...register('phone')} className="input-field" />
                </div>
              </div>
              <div>
                <label className="label">Address</label>
                <textarea {...register('address')} rows={2} className="input-field" />
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea {...register('notes')} rows={3} className="input-field" />
              </div>
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="btn-primary">
                  {isSubmitting ? 'Saving...' : modalType === 'create' ? 'Create Client' : 'Update Client'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
